import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Check, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Layout } from "../components/Layout";
import { billingStatus } from "../lib/api";
import { useAuth } from "../hooks/useAuth";

const MAX_ATTEMPTS = 6;
const INTERVAL = 2000;

export default function ProSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [returnTo, setReturnTo] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }

    let attempts = 0;
    const poll = async () => {
      try {
        const data = await billingStatus(sessionId);
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("success");
          // Return the founder exactly where they left off before subscribing.
          const back = sessionStorage.getItem("lbs_return_after_pro");
          if (back) {
            sessionStorage.removeItem("lbs_return_after_pro");
            setReturnTo(back);
            setTimeout(() => navigate(back), 2200);
          }
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, INTERVAL);
    };
    poll();
  }, [refreshUser]);

  return (
    <Layout>
      <section className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="pro-success-page">
        {state === "checking" && (
          <div data-testid="pro-status-checking">
            <Loader2 size={44} className="mx-auto text-[#D4AF37] animate-spin" />
            <h1 className="font-display text-2xl font-bold text-[#0F0F0F] mt-6">Confirming your payment…</h1>
            <p className="text-[#687280] mt-2">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {state === "success" && (
          <div data-testid="pro-status-success">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center">
              <Crown size={30} />
            </div>
            <h1 className="font-display text-3xl font-extrabold text-[#0F0F0F] mt-6">You're a Founder Pro 🎉</h1>
            <p className="text-[#687280] mt-2">
              Everything is unlocked — unlimited ideas, saves, notes, and Builder Prompts.
            </p>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mt-6 text-sm text-[#0F0F0F]">
              {["Unlimited ideas", "Save & notes", "AI Builder Prompts", "All categories"].map((f) => (
                <span key={f} className="inline-flex items-center gap-2">
                  <Check size={14} className="text-[#D4AF37]" /> {f}
                </span>
              ))}
            </div>
            <button
              onClick={() => navigate(returnTo || "/business-ideas")}
              className="mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-8 py-3.5 font-semibold hover:bg-[#c5a030] transition-colors"
              data-testid="pro-success-browse-btn"
            >
              {returnTo ? "Continue where you left off" : "Start Building"} <ArrowRight size={18} />
            </button>
          </div>
        )}

        {(state === "failed" || state === "timeout") && (
          <div data-testid="pro-status-failed">
            <XCircle size={44} className="mx-auto text-red-500" />
            <h1 className="font-display text-2xl font-bold text-[#0F0F0F] mt-6">
              {state === "timeout" ? "Still processing…" : "Payment not completed"}
            </h1>
            <p className="text-[#687280] mt-2">
              {state === "timeout"
                ? "Your payment is taking longer than expected. If you were charged, your account will update shortly."
                : "Your payment wasn't completed. You can try again anytime."}
            </p>
            <button
              onClick={() => navigate("/profile")}
              className="mt-8 inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-8 py-3.5 font-semibold hover:bg-[#1c1c1c] transition-colors"
              data-testid="pro-success-back-btn"
            >
              Back to Profile
            </button>
          </div>
        )}
      </section>
    </Layout>
  );
}
